// src/components/SEOHead.tsx
import { Helmet } from 'react-helmet-async'

interface Props {
  title: string
  description: string
  canonical: string
  ogImage?: string
  keywords?: string
  schema?: object | object[]
}

const BASE_URL = 'https://www.freshdispense.com'
const DEFAULT_IMAGE = `${BASE_URL}/og-default.jpg`

export default function SEOHead({ title, description, canonical, ogImage, keywords, schema }: Props) {
  const fullCanonical = `${BASE_URL}${canonical}`
  const image = ogImage ? `${BASE_URL}${ogImage}` : DEFAULT_IMAGE

  const schemas = Array.isArray(schema) ? schema : schema ? [schema] : []
  const localBusiness = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${BASE_URL}/#business`,
    name: 'FreshDispense',
    url: BASE_URL,
    telephone: '+17806070170',
    email: 'Contact@freshdispense.com',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Edmonton',
      addressRegion: 'AB',
      addressCountry: 'CA',
    },
    geo: { '@type': 'GeoCoordinates', latitude: 53.5461, longitude: -113.4938 },
    areaServed: [
      { '@type': 'City', name: 'Edmonton', containedInPlace: { '@type': 'State', name: 'Alberta' } },
    ],
    priceRange: '$$',
    openingHoursSpecification: [{
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '17:00',
    }],
    sameAs: [
      'https://www.instagram.com/freshdispense',
      'https://www.facebook.com/freshdispense',
    ],
  }

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <link rel="canonical" href={fullCanonical} />

      {/* Geo / Local */}
      <meta name="geo.region" content="CA-AB" />
      <meta name="geo.placename" content="Edmonton, Alberta" />
      <meta name="geo.position" content="53.5461;-113.4938" />
      <meta name="ICBM" content="53.5461, -113.4938" />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_CA" />
      <meta property="og:site_name" content="FreshDispense.com" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@freshdispense" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Schema.org */}
      <script type="application/ld+json">{JSON.stringify(localBusiness)}</script>
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  )
}
